/**
 * What the company relationship on a journey is actually doing. (W8.1 · F45)
 *
 * THE DEFECT: the card had the company's NAME and nothing about its state, so
 * it rendered "Convidado por Tonnie Talent · Ativo" for two relationships
 * Tonnie Talent had already archived. The name is correct and deliberately
 * kept — a company that archived the relationship still funded the journey —
 * but "Ativo" was a claim nothing supported, derived in the UI from funding
 * source alone.
 *
 * The state now comes from the SERVER (`journey_engagements`), and this
 * component renders it. It computes nothing: no funding-source lookup, no
 * date arithmetic, no inference from the absence of a field.
 *
 * `data-relationship` is the machine-readable half, and it exists because a
 * test must never assert on a CSS class here — `@tonnie/ui` primitives carry
 * variant classes unconditionally, so `class.includes(...)` is true whatever
 * the state is. A missing attribute reads as a failure rather than as a pass.
 *
 * One badge per company: a journey two companies share (D-19) carries each
 * company's own state.
 *
 * Shared by the header switcher and the "My journeys" page, which carry their
 * own test ids for it.
 */
import type { TFunction } from 'i18next';
import { useTranslation } from '@tonnie/locale';
import type { JourneyCompanyRelationship } from '@tonnie/types';
import { Badge, cn } from '@tonnie/ui';

/** The badge's words for each state the server sends — exhaustive, so a new state fails to compile. */
function relationshipLabel(relationship: JourneyCompanyRelationship, t: TFunction): string {
  switch (relationship) {
    case 'live':
      return t('journey.relationship.live');
    case 'archived_by_company':
      return t('journey.relationship.archivedByCompany');
    // D1 — the citizen left the programme, which ended the relationship.
    case 'left':
      return t('journey.relationship.left');
    case 'ended':
      return t('journey.relationship.ended');
    default: {
      const unreachable: never = relationship;
      return unreachable;
    }
  }
}

interface IJourneyRelationshipBadgeProps {
  relationship: JourneyCompanyRelationship;
  testId: string;
  className?: string;
}

export function JourneyRelationshipBadge({
  relationship,
  testId,
  className,
}: IJourneyRelationshipBadgeProps) {
  const { t } = useTranslation();

  const label = relationshipLabel(relationship, t);

  return (
    <Badge
      variant="outline"
      data-testid={testId}
      data-relationship={relationship}
      className={cn(
        'text-xs shrink-0',
        relationship === 'live'
          ? 'bg-success/10 text-success-accessible border-success/20'
          : 'bg-muted text-muted-foreground',
        className
      )}
    >
      {label}
    </Badge>
  );
}
